import React, { useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  Person as PersonIcon,
  WhatsApp as WhatsAppIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { atendimentoService, AtendimentoConexao, AtendimentoTicket } from '../../../services/atendimentoService';
import SetoresAllSelect from './SetoresAllSelect';

interface NovoAtendimentoClienteDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (ticket: AtendimentoTicket) => void;
  contatosCliente: any[];
}

const getInitials = (name?: string | null) => {
  return name ? name.split(' ').slice(0, 2).map((n) => n[0]).join('').toUpperCase() : '?';
};

const formatConnectionStatus = (status: AtendimentoConexao['status']) => {
  if (status === 'conectado') return 'Conectada';
  if (status === 'conectando') return 'Conectando...';
  if (status === 'aguardando_qr') return 'Aguardando QR';
  return 'Desconectada';
};

const NovoAtendimentoClienteDialog: React.FC<NovoAtendimentoClienteDialogProps> = ({ open, onClose, onSuccess, contatosCliente }) => {
  const [conexoes, setConexoes] = useState<AtendimentoConexao[]>([]);
  const [contatoSelecionado, setContatoSelecionado] = useState<any | null>(null);
  const [conexaoId, setConexaoId] = useState<number | ''>('');
  const [setorId, setSetorId] = useState<number | null>(null);
  const [loadingConexoes, setLoadingConexoes] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  useEffect(() => {
    if (!open) return;

    setContatoSelecionado(null);
    setConexaoId('');
    setSetorId(null);
    setErro('');
  }, [open]);

  useEffect(() => {
    if (!open) return;

    let ativo = true;
    const carregarConexoes = async () => {
      setLoadingConexoes(true);
      try {
        const data = await atendimentoService.getConexoes() as AtendimentoConexao[];
        if (!ativo) return;

        const disponiveis = data.filter((conexao) => conexao.ativo && conexao.tipo === 'whatsapp');
        setConexoes(disponiveis);
        const conectada = disponiveis.find((conexao) => conexao.status === 'conectado');
        setConexaoId(conectada?.id || disponiveis[0]?.id || '');
      } catch (error: any) {
        if (ativo) setErro(error?.response?.data?.error || error?.message || 'Erro ao carregar conexões.');
      } finally {
        if (ativo) setLoadingConexoes(false);
      }
    };

    carregarConexoes();
    return () => {
      ativo = false;
    };
  }, [open]);

  const handleClose = () => {
    if (!salvando) onClose();
  };

  const handleIniciar = async () => {
    if (!contatoSelecionado || !conexaoId) return;

    setSalvando(true);
    setErro('');
    try {
      const { reutilizado, ticket } = await atendimentoService.iniciarTicket(contatoSelecionado.id, Number(conexaoId), setorId);
      toast[reutilizado ? 'info' : 'success'](reutilizado ? 'Já existe um atendimento para este contato.' : 'Atendimento iniciado com sucesso!');
      onSuccess(ticket);
    } catch (error: any) {
      setErro(error?.response?.data?.error || error?.response?.data?.message || error?.message || 'Erro ao iniciar atendimento.');
    } finally {
      setSalvando(false);
    }
  };

  const conexaoSelecionada = conexoes.find((conexao) => conexao.id === conexaoId);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: 'blur(4px)',
            backgroundColor: 'rgba(0, 0, 0, 0.2)',
          },
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.1 }}>
        Iniciar atendimento
        <IconButton onClick={handleClose} size="small" disabled={salvando}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ py: 0.75 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 0.25 }}>
          {erro && <Alert severity="error">{erro}</Alert>}

          <Grid container spacing={1} sx={{ pt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small" required error={!loadingConexoes && conexoes.length === 0}>
                <InputLabel>Conexão WhatsApp</InputLabel>
                <Select
                  size="small"
                  value={conexaoId}
                  onChange={(e) => setConexaoId(e.target.value === '' ? '' : Number(e.target.value))}
                  label="Conexão WhatsApp"
                  disabled={salvando || loadingConexoes}
                  renderValue={() => (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.65, minWidth: 0 }}>
                      <WhatsAppIcon sx={{ fontSize: 16, color: conexaoSelecionada?.status === 'conectado' ? '#25d366' : 'text.secondary', flexShrink: 0 }} />
                      <Typography variant="body2" noWrap sx={{ fontSize: '0.8rem', minWidth: 0 }}>
                        {conexaoSelecionada?.nome || 'Selecione'}
                      </Typography>
                    </Box>
                  )}
                  sx={{
                    '& .MuiSelect-select': {
                      py: 0.65,
                      minHeight: '30px !important',
                      display: 'flex',
                      alignItems: 'center',
                    },
                  }}
                >
                  {conexoes.map((conexao) => (
                    <MenuItem key={conexao.id} value={conexao.id} disabled={conexao.status !== 'conectado'} sx={{ py: 0.65 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                        <WhatsAppIcon sx={{ fontSize: 17, color: conexao.status === 'conectado' ? '#25d366' : 'text.secondary', flexShrink: 0 }} />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={600} noWrap sx={{ fontSize: '0.8rem', lineHeight: 1.15 }}>{conexao.nome}</Typography>
                          <Typography variant="caption" noWrap color={conexao.status === 'conectado' ? 'text.secondary' : 'warning.main'} sx={{ display: 'block', fontSize: '0.68rem', lineHeight: 1.15 }}>
                            {[conexao.numero, formatConnectionStatus(conexao.status)].filter(Boolean).join(' - ')}
                          </Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <SetoresAllSelect
                value={setorId}
                onChange={setSetorId}
                disabled={salvando}
                label="Setor"
                placeholder="Selecione o setor"
              />
            </Grid>
          </Grid>

          <Divider />

          <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5 }}>
            Contatos de WhatsApp do Cliente
          </Typography>

          {contatosCliente.length === 0 ? (
            <Box sx={{ textAlign: 'center', p: 3 }}>
              <PersonIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
              <Typography color="text.secondary">
                Nenhum contato de WhatsApp cadastrado para este cliente.
              </Typography>
            </Box>
          ) : (
            <List dense sx={{ maxHeight: 280, overflow: 'auto', p: 0 }}>
              {contatosCliente.map((contato) => {
                const nome = contato.nome || contato.cliente?.nome || contato.numero;

                return (
                  <ListItem key={contato.id} disablePadding>
                    <ListItemButton
                      onClick={() => setContatoSelecionado(contato)}
                      selected={contatoSelecionado?.id === contato.id}
                      disabled={salvando}
                      sx={{ py: 0.55, px: 1, minHeight: 48 }}
                    >
                      <ListItemAvatar sx={{ minWidth: 40 }}>
                        <Avatar src={atendimentoService.getAvatarUrl(contato.foto_perfil)} sx={{ width: 30, height: 30, fontSize: '0.72rem' }}>
                          {!contato.foto_perfil && getInitials(nome)}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={nome}
                        secondary={contato.numero}
                        primaryTypographyProps={{ noWrap: true, fontSize: '0.82rem', fontWeight: 600, lineHeight: 1.15 }}
                        secondaryTypographyProps={{ noWrap: true, fontSize: '0.7rem', lineHeight: 1.15 }}
                        sx={{ my: 0, minWidth: 0 }}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          )}

          {contatoSelecionado && (
            <Paper sx={{ p: 1, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
              <Typography variant="caption" fontWeight={700} sx={{ display: 'block', mb: 0.1 }}>
                Contato selecionado
              </Typography>
              <Typography variant="body2" noWrap sx={{ fontSize: '0.8rem' }}>{contatoSelecionado.nome || contatoSelecionado.cliente?.nome}</Typography>
              <Typography variant="caption" noWrap sx={{ display: 'block', opacity: 0.9, fontSize: '0.68rem' }}>
                {contatoSelecionado.numero}
              </Typography>
            </Paper>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 2, pb: 1.5 }}>
        <Button onClick={handleClose} disabled={salvando}>Cancelar</Button>
        <Button
          variant="contained"
          onClick={handleIniciar}
          disabled={salvando || !contatoSelecionado || !conexaoId}
        >
          {salvando ? 'Iniciando...' : 'Iniciar atendimento'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NovoAtendimentoClienteDialog;
